import { Fragment } from "react";
import {
  type SyndicatorQuestions,
  type SyndicatorQuestionsV2Block,
} from "@lib/config/types";
import { FormField } from "./form/field";
import { FormReviewTopics } from "./form/reviewtopics";
import { type CSSProperties } from "react";
import {
  alignStyle,
  type FontFamily,
  resolveFontFamilyVariables,
  textBlockWidth,
} from "@lib/utils";
import { syndicatorQsFields } from "@lib/syndicator-questions";
import {
  getReviewTopicsForCampaign,
  type SmartPromptsOverride,
} from "@lib/smart-prompts";
import { isPreviewFlagEnabled, IsPreviewUrl } from "@/routes/preview";

// SMART_PROMPTS_AB_TEST_OVERRIDE_TEMP_CODE_START
function readSmartPromptsOverride(): SmartPromptsOverride {
  if (typeof window === "undefined") return undefined;
  const value = new URLSearchParams(window.location.search).get(
    "smartPromptsOverride",
  );
  return value === "on" || value === "off" ? value : undefined;
}
// SMART_PROMPTS_AB_TEST_OVERRIDE_TEMP_CODE_END

// This component renders inside the production review flow as well as
// the config-preview flow. We can't read the preview flag through
// `ConfigRoute.useSearch()` because that route isn't matched on review
// pages and the hook would throw. Read the URL directly when (and only
// when) we're actually on a preview URL.
function readShowSmartPromptsFlag(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    new URLSearchParams(window.location.search).get("showSmartPrompts") ??
    undefined
  );
}

type SyndicatorQuestionsProps = SyndicatorQuestionsV2Block & {
  category?: string;
  subcategory?: string;
  orderId?: string;
};

type SyndicatorQuestionsStyles = CSSProperties & {
  "--label-font-name": string;
  "--label-font-weight": string;
  "--label-font-style": string;
  "--label-color": string;
  "--label-font-size": string;
  "--input-font-name": string;
  "--input-font-weight": string;
  "--input-font-style": string;
  "--input-color": string;
  "--input-background": string;
  "--input-border-color": string;
  "--placeholder-color": string;
  "--field-border-size": string;
  "--field-border-top-left-radius": string;
  "--field-border-top-right-radius": string;
  "--field-border-bottom-right-radius": string;
  "--field-border-bottom-left-radius": string;
};

export function SyndicatorQuestions({
  syndicator_questions,
  category,
  subcategory,
  orderId,
}: SyndicatorQuestionsProps) {
  const inPreview = typeof window !== "undefined" && IsPreviewUrl();
  const ShowTopics = inPreview
    ? isPreviewFlagEnabled(readShowSmartPromptsFlag())
    : true;
  const override = readSmartPromptsOverride();
  const dynamicTopics =
    category && subcategory
      ? getReviewTopicsForCampaign(
          ShowTopics,
          category,
          subcategory,
          orderId,
          override,
        )
      : null;

  const labelFontFamily = syndicator_questions.label_style.font as FontFamily;
  const inputFontFamily = syndicator_questions.input_style.font as FontFamily;

  const transformedFields = syndicatorQsFields(syndicator_questions.fields);

  const reviewTopicsField = transformedFields.find(
    (f): f is typeof f & { topics: string[] } =>
      f.name === "syndicator-question:review_topics",
  );

  const isPill =
    syndicator_questions.input_style.border.radius === 9999.0 ||
    syndicator_questions.input_style.border.radius === 99999.0;

  return (
    <div
      className="grid gap-8"
      style={
        {
          maxWidth: textBlockWidth(syndicator_questions.width),
          ...alignStyle(syndicator_questions),
          ...resolveFontFamilyVariables(
            "--label",
            labelFontFamily,
            syndicator_questions.label_style.font_properties,
          ),
          ...resolveFontFamilyVariables(
            "--input",
            inputFontFamily,
            syndicator_questions.input_style.font_properties,
          ),
          "--label-color": syndicator_questions.label_style.color,
          "--label-font-size": `${(syndicator_questions.label_style as unknown as Record<string, number>).font_size ?? 16}px`,
          "--input-color": syndicator_questions.input_style.color,
          "--input-background":
            syndicator_questions.input_style.background_color,
          "--input-border-color": syndicator_questions.input_style.border.color,
          "--placeholder-color":
            syndicator_questions.input_style.placeholder_color,
          "--field-border-size": `${syndicator_questions.input_style.border.width}px`,
          "--field-border-top-left-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
          "--field-border-top-right-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
          "--field-border-bottom-right-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
          "--field-border-bottom-left-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
        } as SyndicatorQuestionsStyles
      }
    >
      {transformedFields
        .filter((field) => field.name !== "syndicator-question:review_topics")
        .map((field) => (
          <Fragment key={field.name}>
            <FormField {...field} />
            {field.name === "syndicator-question:review_title" && (
              <FormReviewTopics
                id="syndicator-question:review_topics"
                name="syndicator-question:review_topics"
                topics={dynamicTopics ?? reviewTopicsField?.topics ?? []}
              />
            )}
          </Fragment>
        ))}
    </div>
  );
}
